import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "urql";
import { sortBy } from "lodash";
import { Link, useLocation, useParams } from "wouter";

import { exportToXlsx } from "../../../../components/src/common/exportToXlsx";
import { ReactTable } from "../../../../components/src/react-table/ReactTable";
import { createReactTableColumnHelper, ReactTableColumnDef } from "../../../../components/src/react-table/features";
import { DefaultColumnFilter } from "../../../../components/src/react-table/ReactTableFilters";
import { Button } from "../../../../components/src/Button";

import { ACCOUNTING_RUN, ACCOUNTING_RUNS, UPDATE_ACCOUNTING_RUN_STATUS } from "../../../../graphql/src/accountingrun2/queries";
import { CURRENCY_EXPONENT } from "../../../../graphql/src/accountingrun2/currencyExponent";
import { JOURNAL_ENTRIES } from "../../../../graphql/src/journalentry2/queries";
import { DEFAULT_TACCOUNTS } from "../../../../graphql/src/taccountchart2/defaults";

import { userHaveAccessTo } from "../../common/Permissions";
import { formatDecimal } from "./formatDecimal";
import { AccountingBatchType, AccountingRunStatusEnum, CurrencyEnum, PermissionAssetEnum, PermissionTypeEnum } from "../../types.generated";
import type { AccountingRun2, JournalEntry2 } from "../../types.generated";

// opening balance and reversals of the previous run first, then transactions and the closing valuations
const BATCH_ORDER = [
    AccountingBatchType.IB,
    AccountingBatchType.BVI,
    AccountingBatchType.AR,
    AccountingBatchType.MR,
    AccountingBatchType.T,
    AccountingBatchType.BVU,
    AccountingBatchType.A,
    AccountingBatchType.M
];

// same as allowed in updateAccountingRunStatus2
const NEXT_STATUSES: Partial<Record<AccountingRunStatusEnum, AccountingRunStatusEnum[]>> = {
    [AccountingRunStatusEnum.Pending]: [AccountingRunStatusEnum.Preliminary, AccountingRunStatusEnum.Deleted],
    [AccountingRunStatusEnum.Preliminary]: [AccountingRunStatusEnum.Confirmed, AccountingRunStatusEnum.Deleted]
};

const AMOUNT_FIELDS = ["amount", "quantity", "localAmount", "accountingAmount"] as const;

interface PivotRow {
    tAccountNumber: string;
    description: string;
    amounts: Partial<Record<AccountingBatchType | "total", string>>;
}

interface TransactionRow {
    // number of the run of the journal entry
    run: number;
    batch: AccountingBatchType;
    number: number;
    effectiveDate: string;
    description: string;
    externalNumber: number;
    tAccountNumber: string;
    tAccountDescription: string;
    type: string;
    instrument: string;
    // currency of localAmount
    instrumentCurrency: CurrencyEnum;
    amount: string;
    quantity: string;
    localAmount: string;
    accountingAmount: string;
    valueDate: string;
}

export function AccountingRunPage(): React.ReactElement {
    const { clientId, id } = useParams<{ clientId: string; id: string }>();
    const [{ fetching, error, data }, refetch] = useQuery<{ accountingRuns2: AccountingRun2[]; me: { _id: string; roles: any[] } }>({
        query: ACCOUNTING_RUN,
        variables: { id },
        requestPolicy: "network-only"
    });
    const [{ fetching: changingStatus }, updateAccountingRunStatus] = useMutation(UPDATE_ACCOUNTING_RUN_STATUS);
    const [statusError, setStatusError] = useState<string | null>(null);
    const [_, navigate] = useLocation();
    const run = data && data.accountingRuns2 && data.accountingRuns2.length > 0 ? data.accountingRuns2[0] : null;
    const [{ fetching: fetchingRuns, error: runsError, data: runsData }] = useQuery<{ accountingRuns2: AccountingRun2[] }>({
        query: ACCOUNTING_RUNS,
        variables: {
            filter: {
                clientIdIn: [clientId],
                statusIn: [AccountingRunStatusEnum.Pending, AccountingRunStatusEnum.Preliminary, AccountingRunStatusEnum.Confirmed]
            }
        },
        requestPolicy: "network-only"
    });
    // journal entries of all non deleted runs of the period
    const [{ fetching: fetchingJournalEntries, error: journalEntriesError, data: journalEntriesData }] = useQuery<{
        journalEntries2: JournalEntry2[];
    }>({
        query: JOURNAL_ENTRIES,
        variables: { clientId, accountingPeriod: run ? run.accountingPeriod : null },
        requestPolicy: "network-only",
        pause: !run
    });
    // amounts in accounting currency are shown with its ISO 4217 decimals, SEK when not set as in upsertAccountingRun
    const accountingCurrency = (run && (run.client?.accountingCurrency as CurrencyEnum)) || CurrencyEnum.SEK;
    const accountingDecimals = CURRENCY_EXPONENT[accountingCurrency];

    const { batches, pivotRows, transactionRows } = useMemo(() => {
        // the run shows the journal entries of the runs of the period up to and including it, as the balances at the run on the statements
        // page. A later run of the same period adds its transactions and replaces the valuation of the run before it (BVI reverses its BVU)
        const runNumberById: Record<string, number> = {};
        if (run && runsData && runsData.accountingRuns2) {
            for (const periodRun of runsData.accountingRuns2) {
                if (periodRun.accountingPeriod === run.accountingPeriod && periodRun.number <= run.number) {
                    runNumberById[periodRun._id] = periodRun.number;
                }
            }
        }
        const journalEntries = (journalEntriesData && journalEntriesData.journalEntries2 ? journalEntriesData.journalEntries2 : []).filter(
            (journalEntry) => typeof runNumberById[journalEntry.accountingRunId] !== "undefined"
        );
        // a run with the default fund t-account chart has no client chart
        const tAccounts = run && run.clientTAccountChart ? run.clientTAccountChart.tAccounts || [] : DEFAULT_TACCOUNTS;
        const descriptionByNumber: Record<string, string> = {};
        for (const tAccount of tAccounts) {
            descriptionByNumber[tAccount.number] = tAccount.description || "";
        }

        const unitsByNumber: Record<string, Partial<Record<AccountingBatchType | "total", bigint>>> = {};
        const totalUnits: Partial<Record<AccountingBatchType | "total", bigint>> = {};
        const presentBatches = new Set<AccountingBatchType>();
        const transactionRows: TransactionRow[] = [];
        for (const journalEntry of sortBy(
            journalEntries,
            (journalEntry) => runNumberById[journalEntry.accountingRunId],
            (journalEntry) => BATCH_ORDER.indexOf(journalEntry.batch),
            "number"
        )) {
            presentBatches.add(journalEntry.batch);
            for (const transaction of journalEntry.transactions || []) {
                transactionRows.push({
                    run: runNumberById[journalEntry.accountingRunId],
                    batch: journalEntry.batch,
                    number: journalEntry.number,
                    effectiveDate: journalEntry.effectiveDate,
                    description: journalEntry.description,
                    externalNumber: journalEntry.externalNumber,
                    tAccountNumber: transaction.tAccountNumber,
                    tAccountDescription: descriptionByNumber[transaction.tAccountNumber] || "",
                    type: transaction.type,
                    instrument: transaction.instrument?.name || "",
                    instrumentCurrency: transaction.instrument?.currency,
                    amount: transaction.amount,
                    quantity: transaction.quantity,
                    localAmount: transaction.localAmount,
                    accountingAmount: transaction.accountingAmount,
                    valueDate: transaction.valueDate
                });
                if (transaction.amount === null || typeof transaction.amount === "undefined") continue;
                // amounts are exact decimal strings with 4 decimals, summed as bigint in units of 0.0001
                const units = BigInt(transaction.amount.replace(".", ""));
                const accountUnits = unitsByNumber[transaction.tAccountNumber] || (unitsByNumber[transaction.tAccountNumber] = {});
                for (const key of [journalEntry.batch, "total"] as const) {
                    accountUnits[key] = (accountUnits[key] || 0n) + units;
                    totalUnits[key] = (totalUnits[key] || 0n) + units;
                }
            }
        }

        const batches = BATCH_ORDER.filter((batch) => presentBatches.has(batch));
        // bigint units of 0.0001 back to decimal strings with 4 decimals
        const toAmounts = (units: Partial<Record<AccountingBatchType | "total", bigint>>) => {
            const amounts: PivotRow["amounts"] = {};
            for (const [key, value] of Object.entries(units)) {
                const digits = (value < 0n ? -value : value).toString().padStart(5, "0");
                amounts[key] = `${value < 0n ? "-" : ""}${digits.slice(0, -4)}.${digits.slice(-4)}`;
            }
            return amounts;
        };
        const pivotRows: PivotRow[] = Object.keys(unitsByNumber)
            .sort()
            .map((tAccountNumber) => ({
                tAccountNumber,
                description: descriptionByNumber[tAccountNumber] || "",
                amounts: toAmounts(unitsByNumber[tAccountNumber])
            }));
        // all journal entries balance, so the sum over all accounts is zero per batch
        if (pivotRows.length > 0) {
            pivotRows.push({
                tAccountNumber: "Total",
                description: "",
                amounts: toAmounts(totalUnits)
            });
        }
        return { batches, pivotRows, transactionRows };
    }, [run, runsData, journalEntriesData]);

    const pivotColumns = useMemo<ReactTableColumnDef<PivotRow>[]>(() => {
        const columnHelper = createReactTableColumnHelper<PivotRow>();

        return [
            // no column filters, the pivot is short and its Total row should always be visible
            columnHelper.accessor("tAccountNumber", {
                header: "Account",
                size: 100,
                enableColumnFilter: false
            }),
            columnHelper.accessor("description", {
                header: "Description",
                size: 250,
                enableColumnFilter: false
            }),
            ...[...batches, "total" as const].map((key) =>
                columnHelper.accessor((row) => row.amounts[key] || "", {
                    id: key,
                    enableColumnFilter: false,
                    header: key === "total" ? "Total" : key,
                    cell: (cellProps) => (
                        <div style={{ textAlign: "right" }}>{formatDecimal(cellProps.getValue(), accountingDecimals)}</div>
                    ),
                    size: 150
                })
            )
        ];
    }, [batches, accountingDecimals]);

    const transactionColumns = useMemo<ReactTableColumnDef<TransactionRow>[]>(() => {
        const columnHelper = createReactTableColumnHelper<TransactionRow>();

        return [
            columnHelper.accessor("run", { header: "Run", size: 60 }),
            columnHelper.accessor("batch", { header: "Batch", size: 60, filter: DefaultColumnFilter }),
            columnHelper.accessor("number", { header: "Number", size: 75 }),
            columnHelper.accessor("effectiveDate", { header: "Effective date", size: 100 }),
            columnHelper.accessor("description", { header: "Description", size: 200, filter: DefaultColumnFilter }),
            columnHelper.accessor("externalNumber", { header: "External number", size: 75 }),
            columnHelper.accessor("tAccountNumber", { header: "Account", size: 75, filter: DefaultColumnFilter }),
            columnHelper.accessor("tAccountDescription", { header: "Account description", size: 200, filter: DefaultColumnFilter }),
            columnHelper.accessor("type", { header: "Type", size: 125, filter: DefaultColumnFilter }),
            columnHelper.accessor("instrument", { header: "Instrument", size: 200, filter: DefaultColumnFilter }),
            ...AMOUNT_FIELDS.map((field) =>
                columnHelper.accessor(field, {
                    header: field,
                    cell: (cellProps) => {
                        // amount and accountingAmount are in accounting currency, localAmount in instrument currency
                        let decimals: number | null = accountingDecimals;
                        if (field === "quantity") {
                            // stored with 9 decimals, shown without trailing zeros
                            const fraction = (cellProps.getValue() || "").split(".")[1] || "";
                            decimals = fraction.replace(/0+$/, "").length;
                        } else if (field === "localAmount") {
                            decimals = CURRENCY_EXPONENT[cellProps.row.original.instrumentCurrency] ?? null;
                        }
                        return <div style={{ textAlign: "right" }}>{formatDecimal(cellProps.getValue(), decimals)}</div>;
                    },
                    size: 150
                })
            ),
            columnHelper.accessor("valueDate", { header: "Value date", size: 100 })
        ];
    }, [accountingDecimals]);

    if (fetching) return <p>Loading</p>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;
    if (!run) return <p>Accounting run not found</p>;

    const canChangeStatus =
        data.me && userHaveAccessTo(run.clientId, PermissionAssetEnum.ConfirmAccounting, data.me.roles, PermissionTypeEnum.ReadWrite);
    // "four eyes", confirmed by another user than the one who set Preliminary
    const setPreliminaryByMe = data.me && run.updateUserId === data.me._id;

    // amounts as numbers so that they can be summed in Excel
    const exportRun = () => {
        const pivotSheet = pivotRows.map((row) => {
            const sheetRow: Record<string, string | number> = { Account: row.tAccountNumber, Description: row.description };
            for (const key of [...batches, "total" as const]) {
                sheetRow[key === "total" ? "Total" : key] = row.amounts[key] ? Number(row.amounts[key]) : null;
            }
            return sheetRow;
        });
        const transactionsSheet = transactionRows.map((row) => {
            const sheetRow: Record<string, string | number> = { ...row };
            for (const field of AMOUNT_FIELDS) {
                sheetRow[field] = row[field] === null || typeof row[field] === "undefined" ? null : Number(row[field]);
            }
            return sheetRow;
        });
        exportToXlsx(
            [pivotSheet, transactionsSheet],
            `${run.client?.name || run.clientId} ${run.accountingPeriod} run ${run.number}.xlsx`,
            ["T-accounts", "Transactions"]
        );
    };

    return (
        <div>
            <div className="flex gap-4">
                <Link to={`/accountingruns2/${clientId}`}>Accounting runs</Link>
                <Link to={`/accountingruns2/${clientId}/${id}/statements`}>Balance sheet and income statement</Link>
                <Link to={`/accountingruns2/${clientId}/${id}/reconciliation`}>Reconciliation</Link>
                {run.clientTAccountChart ? (
                    <Link to={`/taccountcharts2/${run.clientTAccountChart._id}#tAccountMappings`}>T-account mappings</Link>
                ) : null}
            </div>
            <h4>
                Accounting run {run.number}, {run.client?.name}
            </h4>
            <p>
                Period {run.accountingPeriod}, end date {run.endDate}, status {run.status}, type {run.type}, accounting currency{" "}
                {accountingCurrency}, t-account chart{" "}
                {run.clientTAccountChart
                    ? `${run.clientTAccountChart.name || "(no name)"} version ${run.clientTAccountChart.version}`
                    : "Default fund t-account chart"}
            </p>
            {(NEXT_STATUSES[run.status] || []).length > 0 ? (
                <div className="flex gap-2 mb-2">
                    {NEXT_STATUSES[run.status].map((status) => {
                        const confirmBySameUser = status === AccountingRunStatusEnum.Confirmed && setPreliminaryByMe;
                        let title: string;
                        if (!canChangeStatus) {
                            title = "Requires write permission to ConfirmAccounting";
                        } else if (confirmBySameUser) {
                            title = "Must be confirmed by another user than the one who set Preliminary";
                        }
                        return (
                            <Button
                                key={status}
                                type="button"
                                size="sm"
                                variant={status === AccountingRunStatusEnum.Deleted ? "danger" : "primary"}
                                disabled={!canChangeStatus || confirmBySameUser || changingStatus}
                                title={title}
                                onClick={async () => {
                                    if (!window.confirm(`Do you want to change status of accounting run ${run.number} to ${status}?`))
                                        return;
                                    setStatusError(null);
                                    const result = await updateAccountingRunStatus({ _id: run._id, status });
                                    if (result.error) {
                                        setStatusError(result.error.message);
                                    } else if (status === AccountingRunStatusEnum.Deleted) {
                                        // deleted runs and their journal entries are not shown
                                        navigate(`/accountingruns2/${clientId}`);
                                    } else {
                                        refetch({ requestPolicy: "network-only" });
                                    }
                                }}
                            >
                                Set {status}
                            </Button>
                        );
                    })}
                </div>
            ) : null}
            {statusError ? <div className="form-field-error">{statusError}</div> : null}
            {fetchingRuns || fetchingJournalEntries ? <p>Loading journal entries</p> : null}
            {runsError ? <p>error: {JSON.stringify(runsError, null, 2)}</p> : null}
            {journalEntriesError ? <p>error: {JSON.stringify(journalEntriesError, null, 2)}</p> : null}
            {run.number > 1 ? (
                <p>
                    The journal entries of runs 1 to {run.number} of period {run.accountingPeriod}.
                </p>
            ) : null}
            <h5>T-accounts</h5>
            <ReactTable columns={pivotColumns} data={pivotRows} onExport={exportRun} />
            <h5>Transactions</h5>
            <p>Number of transactions: {formatDecimal(String(transactionRows.length), 0)}</p>
            <ReactTable columns={transactionColumns} data={transactionRows} onExport={exportRun} />
        </div>
    );
}
